import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { DestinationPicker, type DestinationValue } from '../components/DestinationPicker';
import { PageHeader, Row, Section, Select, Tabs, Toggle } from '../components/form';
import { Alert, Badge, Button, Input } from '../components/ui';
import { ApiError, api } from '../lib/api';

interface ParkingLot {
  id: string;
  name: string;
  number: string;
  slotStart: number;
  slotCount: number;
  timeoutSec: number;
}

interface Conference {
  id: string;
  name: string;
  number: string;
  maxMembers: number;
  waitForAdmin: boolean;
  announceJoinLeave: boolean;
  recordCalls: boolean;
}

/** Parking and conference rooms — the two features that hold calls rather than route them. */
export function CallFeatures() {
  const [tab, setTab] = useState('parking');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Call features"
        description="Places a call can sit, and how staff record what it was about."
      />

      <Tabs
        tabs={[
          { id: 'parking', label: 'Parking lots' },
          { id: 'conferences', label: 'Conference rooms' },
          { id: 'dispositions', label: 'Call outcomes' },
          { id: 'autoclip', label: 'AutoCLIP' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'parking' ? (
        <Parking />
      ) : tab === 'conferences' ? (
        <Conferences />
      ) : tab === 'autoclip' ? (
        <Autoclip />
      ) : (
        <Dispositions />
      )}
    </div>
  );
}

function Parking() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data: lots = [] } = useQuery({
    queryKey: ['parking-lots'],
    queryFn: () => api.get<ParkingLot[]>('/api/v1/parking-lots'),
  });

  const columns: Array<Column<ParkingLot>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (l) => <span className="font-medium">{l.name}</span>,
    },
    { key: 'number', header: 'Dial', render: (l) => <code>{l.number}</code> },
    {
      key: 'slots',
      header: 'Slots',
      render: (l) => (
        <span className="tabular-nums">
          {l.slotStart}–{l.slotStart + l.slotCount - 1}
        </span>
      ),
    },
    {
      key: 'timeout',
      header: 'Returns after',
      sort: (a, b) => a.timeoutSec - b.timeoutSec,
      render: (l) => <span className="tabular-nums">{l.timeoutSec}s</span>,
    },
  ];

  return (
    <div className="space-y-4">
      {creating && (
        <ParkingForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['parking-lots'] });
          }}
        />
      )}

      <DataTable
        rows={lots}
        columns={columns}
        rowKey={(l) => l.id}
        search={(l) => `${l.name} ${l.number}`}
        searchPlaceholder="Search lots"
        toolbar={
          <Button onClick={() => setCreating(true)}>
            <Plus size={14} />
            New parking lot
          </Button>
        }
        empty="No parking lots. Create one so calls can be parked and picked up elsewhere."
      />
    </div>
  );
}

function ParkingForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [number, setNumber] = useState('700');
  const [slotStart, setSlotStart] = useState('701');
  const [slotCount, setSlotCount] = useState('10');
  const [timeoutSec, setTimeoutSec] = useState('120');
  const [timeout, setTimeoutDest] = useState<DestinationValue>({
    destinationType: 'hangup',
    destinationId: null,
    destinationValue: null,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/v1/parking-lots', {
        name,
        number,
        slotStart: Number(slotStart),
        slotCount: Number(slotCount),
        timeoutSec: Number(timeoutSec),
        timeout,
      }),
    onSuccess: onCreated,
  });

  const conflict = create.error instanceof ApiError && create.error.isConflict;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Section title="New parking lot">
        <div className="grid gap-4 sm:grid-cols-3">
          <Row label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Main"
              required
            />
          </Row>
          <Row
            label="Park on"
            required
            error={conflict ? 'That number is already in use' : undefined}
            hint="Transfer a call here to park it."
          >
            <Input value={number} onChange={(e) => setNumber(e.target.value)} required />
          </Row>
          <Row label="First slot" required hint="Slots are announced to the parker.">
            <Input
              value={slotStart}
              onChange={(e) => setSlotStart(e.target.value)}
              inputMode="numeric"
            />
          </Row>
          <Row label="Number of slots" required>
            <Input
              value={slotCount}
              onChange={(e) => setSlotCount(e.target.value)}
              inputMode="numeric"
            />
          </Row>
          <Row
            label="Return after (seconds)"
            required
            hint="A forgotten call comes back rather than waiting forever."
          >
            <Input
              value={timeoutSec}
              onChange={(e) => setTimeoutSec(e.target.value)}
              inputMode="numeric"
            />
          </Row>
          <DestinationPicker label="Returns to" value={timeout} onChange={setTimeoutDest} />
        </div>

        {create.error && !conflict && (
          <div className="mt-4">
            <Alert>{(create.error as Error).message}</Alert>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={create.isPending || !name}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Section>
    </form>
  );
}

function Conferences() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: rooms = [] } = useQuery({
    queryKey: ['conferences'],
    queryFn: () => api.get<Conference[]>('/api/v1/conferences'),
  });

  const remove = useMutation({
    mutationFn: async (targets: readonly Conference[]) => {
      for (const t of targets) await api.del(`/api/v1/conferences/${t.id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conferences'] });
      void queryClient.invalidateQueries({ queryKey: ['destinations'] });
    },
  });

  const columns: Array<Column<Conference>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (c) => <span className="font-medium">{c.name}</span>,
    },
    { key: 'number', header: 'Dial', render: (c) => <code>{c.number}</code> },
    {
      key: 'max',
      header: 'Capacity',
      sort: (a, b) => a.maxMembers - b.maxMembers,
      render: (c) => <span className="tabular-nums">{c.maxMembers}</span>,
    },
    {
      key: 'flags',
      header: 'Behaviour',
      render: (c) => (
        <span className="flex flex-wrap gap-1">
          {c.waitForAdmin && <Badge>waits for host</Badge>}
          {c.announceJoinLeave && <Badge>announces</Badge>}
          {c.recordCalls && <Badge tone="warning">records</Badge>}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {editing !== null && (
        <ConferenceForm
          id={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onCreated={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['conferences'] });
            void queryClient.invalidateQueries({ queryKey: ['destinations'] });
          }}
        />
      )}

      <DataTable
        rows={rooms}
        columns={columns}
        rowKey={(c) => c.id}
        search={(c) => `${c.name} ${c.number}`}
        searchPlaceholder="Search rooms"
        toolbar={
          <Button onClick={() => setEditing('new')}>
            <Plus size={14} />
            New conference room
          </Button>
        }
        onEdit={(c) => setEditing(c.id)}
        onDelete={(targets) => remove.mutate(targets)}
        deleteLabel={(targets) =>
          targets.length === 1
            ? `Delete conference room ${targets[0]!.number}? Anyone dialling it will get a fast busy.`
            : `Delete ${targets.length} conference rooms?`
        }
        empty="No conference rooms yet."
      />
    </div>
  );
}

function ConferenceForm({
  id,
  onCancel,
  onCreated,
}: {
  id: string | null;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const creating = id === null;
  const [name, setName] = useState('');
  const [number, setNumber] = useState('800');
  const [maxMembers, setMaxMembers] = useState('20');
  const [waitForAdmin, setWaitForAdmin] = useState(false);
  const [announceJoinLeave, setAnnounceJoinLeave] = useState(true);
  const [recordCalls, setRecordCalls] = useState(false);

  const { data: existing } = useQuery({
    queryKey: ['conference', id],
    queryFn: () => api.get<Conference>(`/api/v1/conferences/${id}`),
    enabled: !creating,
  });

  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setNumber(existing.number);
    setMaxMembers(String(existing.maxMembers));
    setWaitForAdmin(existing.waitForAdmin);
    setAnnounceJoinLeave(existing.announceJoinLeave);
    setRecordCalls(existing.recordCalls);
  }, [existing]);

  const create = useMutation({
    mutationFn: () =>
      (creating ? api.post : api.put)(
        creating ? '/api/v1/conferences' : `/api/v1/conferences/${id}`,
        {
          name,
          number,
          maxMembers: Number(maxMembers),
          waitForAdmin,
          announceJoinLeave,
          recordCalls,
        },
      ),
    onSuccess: onCreated,
  });

  const conflict = create.error instanceof ApiError && create.error.isConflict;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Section title={creating ? 'New conference room' : 'Edit conference room'}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Row label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standup"
              required
            />
          </Row>
          <Row label="Dial" required error={conflict ? 'That number is already in use' : undefined}>
            <Input value={number} onChange={(e) => setNumber(e.target.value)} required />
          </Row>
          <Row label="Capacity" required>
            <Input
              value={maxMembers}
              onChange={(e) => setMaxMembers(e.target.value)}
              inputMode="numeric"
            />
          </Row>
        </div>

        <div className="mt-4 space-y-3">
          <Switch
            label="Wait for the host"
            hint="Callers hear hold music until someone with the host PIN joins."
            checked={waitForAdmin}
            onChange={setWaitForAdmin}
          />
          <Switch
            label="Announce joins and leaves"
            checked={announceJoinLeave}
            onChange={setAnnounceJoinLeave}
          />
          <Switch
            label="Record the conference"
            hint="Stored with call recordings, and subject to the same retention."
            checked={recordCalls}
            onChange={setRecordCalls}
          />
        </div>

        {create.error && !conflict && (
          <div className="mt-4">
            <Alert>{(create.error as Error).message}</Alert>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={create.isPending || !name}>
            {create.isPending ? 'Saving…' : creating ? 'Create' : 'Save'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Section>
    </form>
  );
}

function Switch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <Toggle checked={checked} onChange={onChange} label={label} />
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </div>
  );
}

interface CallDisposition {
  id: string;
  name: string;
  tone: 'neutral' | 'success' | 'warning' | 'destructive';
  sortOrder: number;
  isActive: boolean;
}

const TONES = [
  { value: 'success', label: 'Good outcome' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'warning', label: 'Needs attention' },
  { value: 'destructive', label: 'Bad outcome' },
] as const;

/**
 * The catalogue of outcomes staff tag calls with in the CDR.
 *
 * A list rather than a fixed enum because it differs at every site, and one that can be added to
 * without a release. Removing one deactivates it instead of deleting: calls already tagged with
 * it stay readable, which is what a report covering last quarter needs.
 */
function Dispositions() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [tone, setTone] = useState<CallDisposition['tone']>('neutral');

  const { data: rows = [] } = useQuery({
    queryKey: ['call-dispositions'],
    queryFn: () => api.get<CallDisposition[]>('/api/v1/call-dispositions'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['call-dispositions'] });

  const create = useMutation({
    mutationFn: () => api.post('/api/v1/call-dispositions', { name: name.trim(), tone }),
    onSuccess: () => {
      setName('');
      invalidate();
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/call-dispositions/${id}`),
    onSuccess: invalidate,
  });

  const active = rows.filter((r) => r.isActive);
  const retired = rows.filter((r) => !r.isActive);

  return (
    <div className="space-y-5">
      <Section
        title="Call outcomes"
        description="What a call was about, tagged afterwards. Separate from whether it was answered."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <Row label="Name">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sale, Support, Wrong number…"
              />
            </Row>
          </div>
          <div className="min-w-44">
            <Row label="Colour">
              <Select value={tone} onChange={(v) => setTone(v as CallDisposition['tone'])}>
                {TONES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Row>
          </div>
          <Button
            type="button"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </div>

        {create.error ? (
          <Alert className="mt-3">
            {create.error instanceof Error ? create.error.message : 'Could not add that'}
          </Alert>
        ) : null}

        {active.length > 0 && (
          <ul className="mt-5 divide-y divide-border rounded-[--radius] border border-border">
            {active.map((row) => (
              <li key={row.id} className="flex items-center gap-3 px-4 py-2.5">
                <Badge tone={row.tone === 'neutral' ? 'muted' : row.tone}>{row.name}</Badge>
                <Button
                  type="button"
                  variant="ghost"
                  className="ml-auto text-destructive"
                  onClick={() => deactivate.mutate(row.id)}
                >
                  Retire
                </Button>
              </li>
            ))}
          </ul>
        )}

        {retired.length > 0 && (
          <p className="mt-4 text-xs text-muted-foreground">
            Retired: {retired.map((r) => r.name).join(', ')}. Calls already tagged with these keep
            their tag, but they are no longer offered.
          </p>
        )}
      </Section>
    </div>
  );
}

interface CallingSettings {
  autoclipEnabled: boolean;
  autoclipWindowMinutes: number;
}

/**
 * Returned calls go to whoever placed the original.
 *
 * Worth a screen of its own because it silently overrides the inbound routes: a call from a
 * number somebody dialled recently never reaches the IVR or the queue, and nothing else in the
 * admin explains why.
 */
function Autoclip() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CallingSettings | null>(null);

  const { data } = useQuery({
    queryKey: ['settings', 'calling'],
    queryFn: () => api.get<CallingSettings>('/api/v1/settings/calling'),
  });

  const value = form ?? data ?? null;

  const save = useMutation({
    mutationFn: () => api.put('/api/v1/settings/calling', value),
    onSuccess: () => {
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'calling'] });
    },
  });

  if (!value) return null;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Section
        title="Return calls to the person who called"
        description="When someone calls back a number an extension dialled recently, ring that extension instead of following the inbound route."
      >
        <div className="flex items-center gap-3">
          <Toggle
            checked={value.autoclipEnabled}
            onChange={(autoclipEnabled) => setForm({ ...value, autoclipEnabled })}
            label="Enable AutoCLIP"
          />
          <span className="text-sm">Enable AutoCLIP</span>
        </div>

        <Row
          label="Callback window"
          hint="Minutes after an outbound call during which a call from that number goes to whoever dialled it."
        >
          <Input
            type="number"
            min={5}
            max={1440}
            value={value.autoclipWindowMinutes}
            onChange={(e) => setForm({ ...value, autoclipWindowMinutes: Number(e.target.value) })}
            disabled={!value.autoclipEnabled}
          />
        </Row>

        <Alert tone="warning">
          While this is on, calls from those numbers skip the inbound route entirely — including its
          IVR and queue. If the extension has no device signed in, the call follows the route as
          normal. To test a menu, call from a number nobody has dialled recently, or switch this
          off.
        </Alert>
      </Section>

      {save.error ? <Alert>{(save.error as Error).message}</Alert> : null}

      <div className="flex justify-end gap-2">
        {form !== null && (
          <Button type="button" variant="ghost" onClick={() => setForm(null)}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={form === null || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
